if (customElements.get('lazy-video') === undefined) {
  class LazyVideo extends HTMLElement {
    constructor() {
      super();
      this.autoplay = this.getAttribute('data-autoplay') === 'true';
      this.observer = new IntersectionObserver(this.handleIntersection.bind(this), {
        rootMargin: '100px',
        threshold: 0.1,
      });

      // Store video sources but don't load them yet
      this.videoElement = 
        window.matchMedia('(max-width: 767px)').matches && this.querySelector('.mobile-video')
          ? this.querySelector('.mobile-video')
          : this.querySelector('.desktop-video');
      
      this.isMuted = this.videoElement.muted;

      // Pause the other video when selecting this one
      const otherVideo = window.matchMedia('(max-width: 767px)').matches 
        ? this.querySelector('.desktop-video')
        : this.querySelector('.mobile-video');
      if (otherVideo) {
        otherVideo.pause();
      }

      if (this.videoElement == null) return;
      
      // Save the source and poster attributes
      this.videoSrc = this.videoElement.getAttribute('src');
      // If no direct src attribute, try to get it from source elements
      if (!this.videoSrc) {
        const sourceElement = this.videoElement.querySelector('source');
        if (sourceElement) {
          this.videoSrc = sourceElement.getAttribute('src');
          // sourceElement.removeAttribute('src'); // Remove src to prevent loading
        }
      }
      this.posterSrc = this.videoElement.getAttribute('poster');
      
      // Remove src to prevent loading until needed
      this.videoElement.removeAttribute('src');
      
      // Keep poster for preview
      if (this.posterSrc) {
        this.videoElement.setAttribute('poster', this.posterSrc);
      }

      this.video = this.videoElement; // For compatibility with existing code
      
      this.playButton = document.createElement('button');
      this.playButton.classList.add('play-button');
      this.playButton.setAttribute('aria-label', 'Play video');
      this.playButton.setAttribute('aria-pressed', 'false');

      this.playButton.addEventListener('click', this.togglePlayPause.bind(this));
      this.video.addEventListener('play', () => {
        this.updatePlayButton(true);
      });
      this.video.addEventListener('pause', () => this.updatePlayButton(false));

      // Set up external controls if target specified
      const controlsTarget = this.getAttribute('data-controls-target');
      if (controlsTarget) {
        this.externalControl = document.getElementById(controlsTarget);
        if (this.externalControl) {
          if (this.video.autoplay) {
            this.externalControl.classList.add('is-playing');
          }
          this.externalControl.addEventListener('click', this.togglePlayPause.bind(this));
          this.externalControl.setAttribute('aria-label', 'Play video');
          this.externalControl.setAttribute('aria-pressed', 'false');
        }
      }
    }

    connectedCallback() {
      this.playButton && this.appendChild(this.playButton);
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer.unobserve(this);
    }

    handleIntersection(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadVideo();
          this.observer.unobserve(this);
        }
      });
    }

    loadVideo() {
      if (this.videoSrc && !this.video.hasAttribute('src')) {
        this.video.setAttribute('src', this.videoSrc);
        this.video.setAttribute('preload', 'metadata');
        
        // Check if autoplay is set either via attribute or this.autoplay property
        const hasAutoplayAttr = this.video.hasAttribute('autoplay');
        const willAutoplay = hasAutoplayAttr || this.autoplay;
        
        // Add event listener to know when video is loaded
        const videoReadyHandler = (e) => {
          // If video has autoplay attribute or this.autoplay is true, play it

          if (willAutoplay) {
            if(window.isSafari() && this.isMuted === false){
              const controls = this.dataset.controlsTarget;
              const playPauseButton = document.getElementById(controls);
              if(playPauseButton){
                playPauseButton.classList.remove("is-playing");
              }
            } else {
              this.video.play();
            }
          }
        }

        // Check if browser is Safari on desktop
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isDesktop = window.matchMedia('(min-width: 768px)').matches;
        const isSafariDesktop = isSafari && isDesktop;

        if (isSafari && willAutoplay) {
          const interval = setInterval(() => {
            this.video.play();
            if(this.video.currentTime > 0){
              clearInterval(interval);
            }
          }, 100)
        } else {
          this.video.addEventListener('loadeddata', videoReadyHandler, { once: true });
        }
      }
    }

    playVideo() {
      // Ensure video is loaded before playing
      if (!this.video.hasAttribute('src')) {
        this.loadVideo();
      }
      this.video.play();
    }

    togglePlayPause() {
      // Ensure video is loaded before playing
      if (!this.video.hasAttribute('src')) {
        this.loadVideo();
      }
      
      if (this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }

    updatePlayButton(isPlaying) {
      // Update internal button
      this.playButton.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
      this.playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');

      // Update external control if it exists
      if (this.externalControl) {
        this.externalControl.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
        this.externalControl.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
        if (isPlaying) {
          this.externalControl.classList.add('is-playing');
        } else {
          this.externalControl.classList.remove('is-playing');
        }
      }
    }
  }

  customElements.define('lazy-video', LazyVideo);
}